import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { Button, Icon, Search } from '@equinor/eds-core-react'
import FilterButton from '@/components/filterButton/FilterButton'
import SortButton from '@/components/sortButton/SortButton'
import CardViewButton from '@/components/cardViewButton/CardViewButton'
import ViewFilteredSensorsInEchoButton from '@/components/viewFilteredSensorsInEchoButton/ViewFilteredSensorsInEchoButton'
import styled from 'styled-components'
import Loading from '@/components/loading'
import OnboardingStepper from '@/features/dashboard/components/OnboardingStepper/OnboardingStepper'
import FilterDialog from '@/features/dashboard/components/filterDialog/FilterDialog'
import {
    useGetFilteredTagsService,
    useGetPlants,
    useGetProjectsService,
} from '@/core/hooks-and-services/onboardedSensorsService'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { useGetProtocols } from '@/core/hooks-and-services/protocolsService'
import {
    AreaDto,
    DeviceDto,
    PlantDto,
    ProjectDto,
    SortItem,
    StatusDto,
    StidTagsResponseModel,
    SuperiorTagDto,
    SystemDto,
    InstalledSensorStatus,
    BatteryStatus,
    SignalQuality,
} from '@/core/models/types'
import { HealthFilterOption } from '@/core/models/sensorHealth'
import { useAtom, useSetAtom } from 'jotai'
import {
    searchAtom,
    selectedFilterAreaAtom,
    selectedFilterDeviceAtom,
    selectedFilterProtocolAtom,
    currentPlantAtom,
    selectedFilterProjectAtom,
    selectedFilterSuperiorTagAtom,
    selectedFilterSystemAtom,
    selectedFilterSensorTypeAtom,
    selectedFilterBatteryStatusAtom,
    selectedFilterSignalQualityAtom,
    sortDeviceAtom,
    sortInstallationDateAtom,
    sortTagAtom,
    sortSensorStatusAtom,
    sortBatteryCapacityAtom,
    sortSignalStrengthAtom,
    pageIndexDashboardAtom,
    scannedSerialNumAtom,
    selectedFilterStatusAtom,
    resetInputValuesAtom,
    resetDashboardFiltersAtom,
    tableHeaderSortTagAtom,
    tableHeaderSortDeviceAtom,
    onboardedEditedAtom,
    selectedSensorCardAtom,
    sensorAdditionalDataAtom,
    showErrorFormAtom,
    activeStepAtom,
} from '@/core/store/globalStore'
import { useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'
import SortDialog from '../sortDialog/SortDialog'
import Scanner from '../Scanner'
import { camera } from '@equinor/eds-icons'
import { Sensor } from '../../models'
import ChipRow from './ChipRow'
import { countActiveDashboardFilters } from './dashboardFilterChips'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { mobileActionBarBottom } from '@/styles/layout'

export const SORT_OPTIONS = {
    tag: {
        name: 'tag',
        first: 'az',
        second: 'za',
        selected: 'az',
    },
    installationDate: {
        name: 'installationDate',
        first: 'newest',
        second: 'oldest',
        selected: 'newest',
    },
    device: {
        name: 'device',
        first: 'az',
        second: 'za',
        selected: 'az',
    },
    sensorStatus: {
        name: 'sensorStatus',
        first: 'az',
        second: 'za',
        selected: 'az',
    },
    batteryCapacity: {
        name: 'batteryCapacity',
        first: 'az',
        second: 'za',
        selected: 'az',
    },
    signalStrength: {
        name: 'signalStrength',
        first: 'az',
        second: 'za',
        selected: 'az',
    },
}

const INITIAL_SORT_SPECIFICATION = {
    tag: null,
    installationDate: SORT_OPTIONS.installationDate,
    device: null,
    sensorStatus: null,
    batteryCapacity: null,
    signalStrength: null,
}

const INITIAL_FILTERS = {
    status: [] as (InstalledSensorStatus | string)[],
    plant: '',
    project: '',
    device: '',
    protocol: '',
    system: '',
    area: '',
    superiorTag: '',
    sensorType: '',
    batteryStatus: '' as BatteryStatus | '',
    signalQuality: '' as SignalQuality | '',
    tags: [] as StidTagsResponseModel[],
}

const EMPTY_TAGS: StidTagsResponseModel[] = []

const STATUS_OPTIONS: StatusDto[] = [
    { displayName: 'Active sensors', value: InstalledSensorStatus.Active },
    { displayName: 'Prepared sensors', value: InstalledSensorStatus.Prepared },
    { displayName: 'Drafted sensors', value: InstalledSensorStatus.Draft },
    { displayName: 'Offline sensors', value: InstalledSensorStatus.Offline },
    { displayName: 'Unsuccessful sensors', value: InstalledSensorStatus.Unsuccessful },
    { displayName: 'Deleted sensors', value: InstalledSensorStatus.Deleted },
]

interface TableBarProps {
    cardViewButtonIcon: string
    onFilter: (filters: typeof INITIAL_FILTERS) => void
    onToggle: () => void
    handleScan: (data: any) => void
    setScannerFetchingData?: (value: boolean | ((prevState: boolean) => boolean)) => void
    sensors?: Sensor[]
    filteredSensors: Sensor[]
    sortSpecification: {
        tag: SortItem
        installationDate: SortItem
        device: SortItem
        sensorStatus: SortItem
        batteryCapacity: SortItem
        signalStrength: SortItem
    }
    setSortSpecification: React.Dispatch<
        React.SetStateAction<{
            tag: SortItem
            installationDate: SortItem
            device: SortItem
            sensorStatus: SortItem
            batteryCapacity: SortItem
            signalStrength: SortItem
        }>
    >
}

export interface TableBarRef {
    onEditSensor: (sensor: Sensor) => void
    openAddSensor: () => void
}

const TableBar = forwardRef<TableBarRef, TableBarProps>(
    (
        {
            cardViewButtonIcon,
            onFilter,
            onToggle,
            handleScan,
            setScannerFetchingData,
            sensors = [],
            filteredSensors,
            sortSpecification = INITIAL_SORT_SPECIFICATION,
            setSortSpecification,
        },
        ref
    ) => {
        const windowWidth = useWindowWidth()

        const [menuOpen, setMenuOpen] = useState(false)
        const [filterOpen, setFilterOpen] = useState(false)
        const [filterPopupOpen, setFilterPopupOpen] = useState(false)
        const [sortOpen, setSortOpen] = useState(false)
        const [sortPopupOpen, setSortPopupOpen] = useState(false)
        const [sensorToEdit, setSensorToEdit] = useState<Sensor | null>(null)

        const { data: plants, error, isLoading } = useGetPlants()
        const { data: devices } = useGetDevices()
        const { data: protocols, isLoading: protocolsLoading } = useGetProtocols()

        const [selectedStatuses, setSelectedStatuses] = useAtom(selectedFilterStatusAtom)
        const [selectedPlant] = useAtom(currentPlantAtom)
        const { setCurrentPlant } = useCurrentPlant()
        const [selectedDevice, setSelectedDevice] = useAtom(selectedFilterDeviceAtom)
        const [selectedProtocol, setSelectedProtocol] = useAtom(selectedFilterProtocolAtom)
        const [selectedProject, setSelectedProject] = useAtom(selectedFilterProjectAtom)
        const [selectedSystem, setSelectedSystem] = useAtom(selectedFilterSystemAtom)
        const [selectedArea, setSelectedArea] = useAtom(selectedFilterAreaAtom)
        const [selectedSuperiorTag, setSelectedSuperiorTag] = useAtom(selectedFilterSuperiorTagAtom)
        const [selectedSensorType, setSelectedSensorType] = useAtom(selectedFilterSensorTypeAtom)
        const [selectedBatteryStatus, setSelectedBatteryStatus] = useAtom(selectedFilterBatteryStatusAtom)
        const [selectedSignalQuality, setSelectedSignalQuality] = useAtom(selectedFilterSignalQualityAtom)

        const activeFilterCount = countActiveDashboardFilters({
            statuses: selectedStatuses,
            device: selectedDevice,
            sensorType: selectedSensorType,
            protocol: selectedProtocol,
            project: selectedProject,
            superiorTag: selectedSuperiorTag,
            system: selectedSystem,
            area: selectedArea,
            batteryStatus: selectedBatteryStatus,
            signalQuality: selectedSignalQuality,
        })

        const [sortTag, setSortTag] = useAtom(sortTagAtom)
        const [sortInstallationDate, setSortInstallationDate] = useAtom(sortInstallationDateAtom)
        const [sortDevice, setSortDevice] = useAtom(sortDeviceAtom)
        const [sortSensorStatus, setSortSensorStatus] = useAtom(sortSensorStatusAtom)
        const [sortBatteryCapacity, setSortBatteryCapacity] = useAtom(sortBatteryCapacityAtom)
        const [sortSignalStrength, setSortSignalStrength] = useAtom(sortSignalStrengthAtom)

        const [tableHeaderSortTag, setTableHeaderSortTag] = useAtom(tableHeaderSortTagAtom)
        const [tableHeaderSortDevice, setTableHeaderSortDevice] = useAtom(tableHeaderSortDeviceAtom)

        const [search, setSearch] = useAtom(searchAtom)
        const [scannedSerialNum, setScannedSerialNumber] = useAtom(scannedSerialNumAtom)
        const [, setCurrentPageIndex] = useAtom(pageIndexDashboardAtom)

        const { data: projects = [], isLoading: projectsLoading } = useGetProjectsService(
            selectedPlant?.shortName || ''
        )

        const { data: tagsData } = useGetFilteredTagsService(
            selectedPlant?.shortName || '',
            selectedProject?.projectCode || '',
            selectedDevice?.key || '',
            selectedSystem?.systemCode || '',
            selectedArea?.areaCode || ''
        )
        const tags = tagsData ?? EMPTY_TAGS

        const sortRef = useRef<HTMLDivElement>(null)
        const filterRef = useRef<HTMLDivElement>(null)
        const resetInputValues = useSetAtom(resetInputValuesAtom)
        const [, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)
        const [, setSensorAdditionalData] = useAtom(sensorAdditionalDataAtom)
        const [onboardedEdited, setOnboardedEdited] = useAtom(onboardedEditedAtom)
        const setShowErrorForm = useSetAtom(showErrorFormAtom)
        const setActiveStep = useSetAtom(activeStepAtom)

        const [pendingSensor, setPendingSensor] = useState<{
            serialNumber: string
            isDraft: boolean
            plantShortName?: string
        } | null>(null)

        const plantByShortName = useCallback(
            (plantShortName?: string) => {
                if (!plantShortName) return null

                return (
                    plants?.find((plant) => plant.shortName === plantShortName) ??
                    ({ shortName: plantShortName } as PlantDto)
                )
            },
            [plants]
        )

        const clearFilters = useSetAtom(resetDashboardFiltersAtom)

        const openSensorDetails = useCallback(
            (sensor: Sensor, plantShortName?: string) => {
                setSensorToEdit(null)
                setMenuOpen(false)
                setOnboardedEdited(false)
                setSensorAdditionalData([])
                void setCurrentPlant(plantByShortName(plantShortName))
                setSelectedSensorCard({ sensor, editOnboarded: false, show: true })
            },
            [plantByShortName, setCurrentPlant]
        )

        const handleOpenSensor = useCallback(
            (serialNumber: string, isDraft: boolean, plantShortName?: string) => {
                const normalizedSerial = serialNumber?.trim()
                clearFilters()
                if (plantShortName) {
                    void setCurrentPlant(plantByShortName(plantShortName))
                }
                setSearch(normalizedSerial)
                setCurrentPageIndex(1)
                setPendingSensor({ serialNumber: normalizedSerial, isDraft, plantShortName })
            },
            [clearFilters, plantByShortName, setCurrentPlant]
        )

        const handleOpenDraft = useCallback(
            (serialNumber: string, plantShortName?: string) => {
                handleOpenSensor(serialNumber, true, plantShortName)
            },
            [handleOpenSensor]
        )

        const handleOpenCreatedSensor = useCallback(
            (serialNumber: string, plantShortName?: string) => {
                handleOpenSensor(serialNumber, false, plantShortName)
            },
            [handleOpenSensor]
        )

        useEffect(() => {
            if (!pendingSensor) return
            const normalizedPendingSerial = pendingSensor.serialNumber.trim().toLowerCase()

            const sensorBySerial = sensors.find(
                (s) => (s.vendorSerialNumber ?? '').trim().toLowerCase() === normalizedPendingSerial
            )

            const matchingSensor = pendingSensor.isDraft
                ? (sensors.find(
                      (s) =>
                          (s.vendorSerialNumber ?? '').trim().toLowerCase() === normalizedPendingSerial &&
                          s.status === InstalledSensorStatus.Draft
                  ) ?? sensorBySerial)
                : sensorBySerial

            if (matchingSensor) {
                const targetPlantShortName = pendingSensor.plantShortName ?? matchingSensor.plant ?? undefined
                setPendingSensor(null)
                openSensorDetails(matchingSensor, targetPlantShortName)
            }
        }, [pendingSensor, sensors, openSensorDetails])

        useEffect(() => {
            onFilter({
                status: selectedStatuses
                    .map((status) => status.value)
                    .filter((value): value is InstalledSensorStatus => value != null),
                plant: selectedPlant?.shortName || '',
                project: selectedProject?.projectCode || '',
                device: selectedDevice?.key || '',
                protocol: selectedProtocol || '',
                system: selectedSystem?.systemCode || '',
                area: selectedArea?.areaCode || '',
                superiorTag: selectedSuperiorTag?.superiorTag || '',
                sensorType: selectedSensorType || '',
                batteryStatus: selectedBatteryStatus || '',
                signalQuality: selectedSignalQuality || '',
                tags: tags,
            })
        }, [
            tags,
            selectedStatuses,
            selectedPlant,
            selectedDevice,
            selectedProtocol,
            selectedProject,
            selectedSystem,
            selectedArea,
            selectedSuperiorTag,
            selectedSensorType,
            selectedBatteryStatus,
            selectedSignalQuality,
        ])

        useEffect(() => {
            if (
                sortSpecification.installationDate == null ||
                sortSpecification.tag == null ||
                sortSpecification.device == null ||
                sortSpecification.sensorStatus == null ||
                sortSpecification.batteryCapacity == null ||
                sortSpecification.signalStrength == null
            ) {
                return
            }
            if (sortSpecification.tag?.selected) {
                setSortInstallationDate({ ...SORT_OPTIONS.installationDate, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: null })
                setSortTag({ ...SORT_OPTIONS.tag, selected: sortSpecification.tag.selected })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: null })
                setSortBatteryCapacity({ ...SORT_OPTIONS.batteryCapacity, selected: null })
                setSortSignalStrength({ ...SORT_OPTIONS.signalStrength, selected: null })
            }
            if (sortSpecification.installationDate.selected) {
                setSortTag({ ...SORT_OPTIONS.tag, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: null })
                setSortInstallationDate({
                    ...SORT_OPTIONS.installationDate,
                    selected: sortSpecification.installationDate.selected,
                })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: null })
                setSortBatteryCapacity({ ...SORT_OPTIONS.batteryCapacity, selected: null })
                setSortSignalStrength({ ...SORT_OPTIONS.signalStrength, selected: null })
            }
            if (sortSpecification.device.selected) {
                setSortTag({ ...SORT_OPTIONS.tag, selected: null })
                setSortInstallationDate({ ...SORT_OPTIONS.installationDate, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: sortSpecification.device.selected })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: null })
                setSortBatteryCapacity({ ...SORT_OPTIONS.batteryCapacity, selected: null })
                setSortSignalStrength({ ...SORT_OPTIONS.signalStrength, selected: null })
            }
            if (sortSpecification.sensorStatus.selected) {
                setSortTag({ ...SORT_OPTIONS.tag, selected: null })
                setSortInstallationDate({ ...SORT_OPTIONS.installationDate, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: null })
                setSortBatteryCapacity({ ...SORT_OPTIONS.batteryCapacity, selected: null })
                setSortSignalStrength({ ...SORT_OPTIONS.signalStrength, selected: null })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: sortSpecification.sensorStatus.selected })
            }
            if (sortSpecification.batteryCapacity.selected) {
                setSortTag({ ...SORT_OPTIONS.tag, selected: null })
                setSortInstallationDate({ ...SORT_OPTIONS.installationDate, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: null })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: null })
                setSortSignalStrength({ ...SORT_OPTIONS.signalStrength, selected: null })
                setSortBatteryCapacity({
                    ...SORT_OPTIONS.batteryCapacity,
                    selected: sortSpecification.batteryCapacity.selected,
                })
            }
            if (sortSpecification.signalStrength.selected) {
                setSortTag({ ...SORT_OPTIONS.tag, selected: null })
                setSortInstallationDate({ ...SORT_OPTIONS.installationDate, selected: null })
                setSortDevice({ ...SORT_OPTIONS.device, selected: null })
                setSortSensorStatus({ ...SORT_OPTIONS.sensorStatus, selected: null })
                setSortBatteryCapacity({ ...SORT_OPTIONS.batteryCapacity, selected: null })
                setSortSignalStrength({
                    ...SORT_OPTIONS.signalStrength,
                    selected: sortSpecification.signalStrength.selected,
                })
            }
        }, [sortSpecification])

        useEffect(() => {
            setSearch(scannedSerialNum?.serialNumber)
        }, [scannedSerialNum])

        const handleOnSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setSearch(value)
            setScannedSerialNumber(null)
            setCurrentPageIndex(1)
        }

        const handleSelectionChange = useCallback(
            (
                type:
                    | 'status'
                    | 'device'
                    | 'protocol'
                    | 'project'
                    | 'system'
                    | 'area'
                    | 'superiorTag'
                    | 'batteryStatus'
                    | 'signalQuality'
                    | 'sensorType',
                changes: { selectedItems: unknown[] }
            ) => {
                if (type === 'status') {
                    const items = changes.selectedItems as StatusDto[]
                    const uniqueByValue = items.filter(
                        (item, index) => items.findIndex((other) => other.value === item.value) === index
                    )
                    setSelectedStatuses(uniqueByValue)
                    setCurrentPageIndex(1)
                    setSearch('')
                    return
                }
                const selectedItem = changes.selectedItems[0] ?? null
                if (selectedItem) {
                    switch (type) {
                        case 'device':
                            setSelectedDevice(selectedItem as DeviceDto)
                            break
                        case 'protocol':
                            setSelectedProtocol(selectedItem as string)
                            break
                        case 'project':
                            setSelectedProject(selectedItem as ProjectDto)
                            break
                        case 'system':
                            setSelectedSystem(selectedItem as SystemDto)
                            break
                        case 'area':
                            setSelectedArea(selectedItem as AreaDto)
                            break
                        case 'superiorTag':
                            setSelectedSuperiorTag(selectedItem as SuperiorTagDto)
                            break
                        case 'sensorType':
                            setSelectedSensorType(selectedItem as string)
                            break
                        case 'batteryStatus':
                            setSelectedBatteryStatus((selectedItem as HealthFilterOption<BatteryStatus>).value)
                            break
                        case 'signalQuality':
                            setSelectedSignalQuality((selectedItem as HealthFilterOption<SignalQuality>).value)
                            break
                    }
                }
                setCurrentPageIndex(1)
                setSearch('')
            },
            []
        )

        const handleSortChange = useCallback(
            (
                type: 'tag' | 'installationDate' | 'device' | 'sensorStatus' | 'batteryCapacity' | 'signalStrength',
                selected?: string
            ) => {
                const newSort = { ...SORT_OPTIONS[type], selected: selected || null }

                setSortSpecification((prev) => ({
                    tag: type === 'tag' ? newSort : { ...SORT_OPTIONS.tag, selected: null },
                    installationDate:
                        type === 'installationDate' ? newSort : { ...SORT_OPTIONS.installationDate, selected: null },
                    device: type === 'device' ? newSort : { ...SORT_OPTIONS.device, selected: null },
                    sensorStatus: type === 'sensorStatus' ? newSort : { ...SORT_OPTIONS.sensorStatus, selected: null },
                    batteryCapacity:
                        type === 'batteryCapacity' ? newSort : { ...SORT_OPTIONS.batteryCapacity, selected: null },
                    signalStrength:
                        type === 'signalStrength' ? newSort : { ...SORT_OPTIONS.signalStrength, selected: null },
                }))
            },
            []
        )

        const { showConfirm: showConfirmAddNewSensor } = useConfirmDialog({
            title: 'Unsaved changes',
            content: 'You have unsaved changes. Are you sure you want to proceed?',
            rejectButtonTitle: 'Discard changes',
            confirmButtonTitle: 'Stay on page',
            onReject: () => {
                setSelectedSensorCard(null)
                setSensorAdditionalData([])
                setOnboardedEdited(false)
                resetInputValues()
                setSensorToEdit(null)
                setMenuOpen(true)
            },
            onConfirm: () => {},
            autoClose: true,
        })

        const openAddSensor = useCallback(() => {
            if (onboardedEdited) {
                showConfirmAddNewSensor()
                return
            }

            resetInputValues()
            setShowErrorForm(false)
            setActiveStep(0)
            setSensorToEdit(null)
            setMenuOpen(true)
        }, [onboardedEdited, resetInputValues, setShowErrorForm, setActiveStep, showConfirmAddNewSensor])

        useImperativeHandle(ref, () => ({
            onEditSensor: (sensor: Sensor) => {
                setSensorToEdit(sensor)
                setMenuOpen(true)
            },
            openAddSensor,
        }))

        // Only react to actual table header toggles, so the initial values don't override the default sort.
        const previousTableHeaderSortTag = useRef(tableHeaderSortTag)
        const previousTableHeaderSortDevice = useRef(tableHeaderSortDevice)

        useEffect(() => {
            if (previousTableHeaderSortTag.current === tableHeaderSortTag) return
            previousTableHeaderSortTag.current = tableHeaderSortTag
            handleSortChange('tag', tableHeaderSortTag === 'A-Z' ? SORT_OPTIONS.tag.first : SORT_OPTIONS.tag.second)
        }, [tableHeaderSortTag, handleSortChange])

        useEffect(() => {
            if (previousTableHeaderSortDevice.current === tableHeaderSortDevice) return
            previousTableHeaderSortDevice.current = tableHeaderSortDevice
            handleSortChange(
                'device',
                tableHeaderSortDevice === 'A-Z' ? SORT_OPTIONS.device.first : SORT_OPTIONS.device.second
            )
        }, [tableHeaderSortDevice, handleSortChange])

        const handleClear = useCallback(
            (
                type:
                    | 'status'
                    | 'device'
                    | 'protocol'
                    | 'project'
                    | 'system'
                    | 'area'
                    | 'superiorTag'
                    | 'batteryStatus'
                    | 'signalQuality'
                    | 'sensorType'
            ) => {
                switch (type) {
                    case 'status':
                        setSelectedStatuses([])
                        break
                    case 'device':
                        setSelectedDevice(null)
                        break
                    case 'protocol':
                        setSelectedProtocol('')
                        break
                    case 'project':
                        setSelectedProject(null)
                        break
                    case 'system':
                        setSelectedSystem(null)
                        break
                    case 'area':
                        setSelectedArea(null)
                        break
                    case 'superiorTag':
                        setSelectedSuperiorTag(null)
                        break
                    case 'sensorType':
                        setSelectedSensorType(null)
                        break
                    case 'batteryStatus':
                        setSelectedBatteryStatus(null)
                        break
                    case 'signalQuality':
                        setSelectedSignalQuality(null)
                        break
                }
            },
            []
        )

        if (isLoading) {
            return <Loading />
        }

        const ScannerButton = (
            <ScannerStyledButton color="secondary">
                <StyledIcon data={camera} size={24} />
                Scan sensor
            </ScannerStyledButton>
        )

        return (
            <Container>
                {windowWidth > 1024 ? (
                    <>
                        <DesktopContainer>
                            <AddNewSensorButton color={'primary'} onClick={openAddSensor}>
                                <Icon name="add"></Icon>
                                {windowWidth <= 1440 ? 'Add sensor' : 'Add new sensor'}
                            </AddNewSensorButton>
                            <ViewFilteredSensorsInEchoButton filteredSensors={filteredSensors} />
                            <ButtonGroup>
                                <Search
                                    aria-label="Search"
                                    placeholder="Search"
                                    onChange={handleOnSearchValueChange}
                                    value={search || ''}
                                />
                                <Scanner
                                    onScan={handleScan}
                                    onFetchingData={setScannerFetchingData}
                                    scannerButton={ScannerButton}
                                    isReadSensor={true}
                                />
                                <Pointer ref={sortRef}>
                                    <SortButton onClick={() => setSortPopupOpen(true)} />
                                </Pointer>
                                <Pointer ref={filterRef}>
                                    <FilterButton onClick={() => setFilterPopupOpen(true)} count={activeFilterCount} />
                                </Pointer>
                                <CardViewButton cardViewButtonIcon={cardViewButtonIcon} onToggle={onToggle} />
                            </ButtonGroup>
                        </DesktopContainer>
                        <ChipRow />
                    </>
                ) : (
                    <>
                        <AddNewSensorButton
                            onClick={() => {
                                setSortOpen(false)
                                setFilterOpen(false)
                                openAddSensor()
                            }}
                        >
                            <Icon name="add"></Icon>
                            {windowWidth > 330 ? 'Add sensor' : ''}
                        </AddNewSensorButton>
                        <Pointer ref={sortRef}>
                            <SortButton
                                onClick={() => {
                                    ;(setSortOpen(true), setFilterOpen(false), setMenuOpen(false))
                                }}
                            />
                        </Pointer>
                        <Pointer ref={filterRef}>
                            <FilterButton
                                onClick={() => {
                                    ;(setSortOpen(false), setFilterOpen(true), setMenuOpen(false))
                                }}
                                count={activeFilterCount}
                            />
                        </Pointer>
                    </>
                )}
                <FilterDialog
                    showMenu={filterOpen}
                    onSelectionChange={handleSelectionChange}
                    onClear={handleClear}
                    onClose={() => setFilterOpen(false)}
                    statuses={STATUS_OPTIONS}
                    projects={projects ?? []}
                    devices={devices ?? []}
                    protocols={protocols ?? []}
                    projectsLoading={projectsLoading}
                    protocolsLoading={protocolsLoading}
                    selectedStatus={selectedStatuses}
                    selectedProject={selectedProject}
                    selectedDevice={selectedDevice}
                    selectedProtocol={selectedProtocol}
                    selectedSystem={selectedSystem}
                    selectedArea={selectedArea}
                    selectedSuperiorTag={selectedSuperiorTag}
                    selectedSensorType={selectedSensorType}
                    selectedBatteryStatus={selectedBatteryStatus}
                    selectedSignalQuality={selectedSignalQuality}
                    anchor={null}
                />
                <FilterDialog
                    showMenu={filterPopupOpen}
                    onSelectionChange={handleSelectionChange}
                    onClear={handleClear}
                    onClose={() => setFilterPopupOpen(false)}
                    statuses={STATUS_OPTIONS}
                    projects={projects ?? []}
                    devices={devices ?? []}
                    protocols={protocols ?? []}
                    projectsLoading={projectsLoading}
                    protocolsLoading={protocolsLoading}
                    selectedStatus={selectedStatuses}
                    selectedProject={selectedProject}
                    selectedDevice={selectedDevice}
                    selectedProtocol={selectedProtocol}
                    selectedSystem={selectedSystem}
                    selectedArea={selectedArea}
                    selectedSuperiorTag={selectedSuperiorTag}
                    selectedSensorType={selectedSensorType}
                    selectedBatteryStatus={selectedBatteryStatus}
                    selectedSignalQuality={selectedSignalQuality}
                    anchor={filterRef.current}
                />
                <SortDialog
                    showMenu={sortPopupOpen}
                    onSortChange={handleSortChange}
                    onClose={() => setSortPopupOpen(false)}
                    sortTag={sortTag}
                    sortInstallationDate={sortInstallationDate}
                    sortDevice={sortDevice}
                    sortSensorStatus={sortSensorStatus}
                    sortBatteryCapacity={sortBatteryCapacity}
                    sortSignalStrength={sortSignalStrength}
                    anchor={sortRef.current}
                />
                <SortDialog
                    showMenu={sortOpen}
                    onSortChange={handleSortChange}
                    onClose={() => setSortOpen(false)}
                    sortTag={sortTag}
                    sortInstallationDate={sortInstallationDate}
                    sortDevice={sortDevice}
                    sortSensorStatus={sortSensorStatus}
                    sortBatteryCapacity={sortBatteryCapacity}
                    sortSignalStrength={sortSignalStrength}
                    anchor={null}
                />
                <OnboardingStepper
                    showMenu={menuOpen}
                    onClose={() => {
                        setSelectedSensorCard(null)
                        setMenuOpen(false)
                    }}
                    onReopen={() => setMenuOpen(true)}
                    onOpenDraft={handleOpenDraft}
                    onOpenCreatedSensor={handleOpenCreatedSensor}
                    plants={plants ?? []}
                    devices={devices ?? []}
                    sensorToEdit={sensorToEdit}
                />
            </Container>
        )
    }
)

const Pointer = styled.div`
    cursor: pointer;
`

export const AddNewSensorButton = styled(Button)`
    width: 168px;
    height: 38px;
    flex-shrink: 0;
    white-space: nowrap;

    @media (min-width: 1025px) and (max-width: 1440px) {
        width: auto;
    }

    @media (max-width: 1024px) {
        width: 140px;
        height: 40px;
        &:hover {
            border-color: #004f55;
            background-color: #004f55;
        }
    }

    @media (max-width: 400px) {
        width: 43%;
        height: 40px;
    }

    @media (max-width: 330px) {
        width: 25%;
        height: 40px;
    }
`

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 0 0 1rem 0;
    align-items: center;

    @media (max-width: 1024px) {
        width: calc(100% - 32px);
        flex-wrap: nowrap;
        position: fixed;
        bottom: ${mobileActionBarBottom};
        left: 50%;
        transform: translateX(-50%);
        z-index: 9;
        border-radius: 8px;
        box-sizing: border-box;
        padding: 8px 8px;
        justify-content: space-between;
        background-color: #ffffff;
        border: 1px solid #d4d4d4;
        box-shadow: 0px 2px 4px 0px #00000024;
    }
`

const DesktopContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
`

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: auto;
    gap: 16px;

    @media (max-width: 1024px) {
        margin-left: 0;
        gap: 1rem;
    }

    @media (max-width: 400px) {
        gap: 8px;
        width: 100%;
    }
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: space-evenly;
    height: 22px;
    width: 24px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        &:hover {
            background: #243746;
            color: white;
        }
    }
`

const ScannerStyledButton = styled(Button)`
    width: 144px;
    height: 38px;
    padding: 0px;
    margin-left: 16px;
`

export default TableBar
